import React, { useEffect, useState } from 'react';
import { Briefcase, TrendingUp, TrendingDown, Clock, ArrowRight } from 'lucide-react';

const Portfolio = ({ userData }: { userData?: any }) => {
  const [history, setHistory] = useState<any[]>([]);

  useEffect(() => {
    if (userData && userData.id) {
      fetch(`https://motilalalgo.com/api/profile/${userData.id}/portfolio-history`)
        .then(res => res.json())
        .then(data => setHistory(data))
        .catch(err => console.error(err));
    }
  }, [userData]);

  if (!userData) return null;

  const currentBalance = parseFloat(userData.wallet_balance || 0);
  
  // Calculate total lifetime profit and loss from history
  const totalProfit = history.filter(h => h.type === 'PROFIT').reduce((acc, curr) => acc + parseFloat(curr.amount), 0);
  const totalLoss = history.filter(h => h.type === 'LOSS').reduce((acc, curr) => acc + parseFloat(curr.amount), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white">Your Portfolio</h1>
        <p className="text-gray-400 mt-1">Track your current balance and P&L history.</p>
      </div>

      {/* Portfolio Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#131b2c] border border-gray-800 rounded-2xl p-6 shadow-xl relative overflow-hidden">
          <div className="absolute -right-4 -bottom-4 opacity-10"><Briefcase size={80} /></div>
          <div className="flex items-center gap-3 mb-4"><Briefcase className="text-blue-500" size={20} /><h3 className="text-gray-400 font-bold uppercase text-xs tracking-wider">Current Value</h3></div>
          <h2 className="text-4xl font-black text-white">₹{currentBalance.toFixed(2)}</h2>
        </div>
        
        <div className="bg-[#0b1221] border border-green-500/20 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-4"><TrendingUp className="text-green-500" size={20} /><h3 className="text-gray-400 font-bold uppercase text-xs tracking-wider">Lifetime Profit</h3></div>
          <h2 className="text-3xl font-bold text-green-400">+₹{totalProfit.toFixed(2)}</h2>
        </div>

        <div className="bg-[#0b1221] border border-red-500/20 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-4"><TrendingDown className="text-red-500" size={20} /><h3 className="text-gray-400 font-bold uppercase text-xs tracking-wider">Lifetime Loss</h3></div>
          <h2 className="text-3xl font-bold text-red-400">-₹{totalLoss.toFixed(2)}</h2>
        </div>
      </div>

      {/* Profit & Loss History Ledger */}
      <div className="bg-[#131b2c] border border-gray-800 rounded-2xl overflow-hidden shadow-xl">
        <div className="p-6 border-b border-gray-800 flex items-center gap-3">
          <Clock className="text-blue-500" size={24} />
          <h3 className="text-xl font-bold text-white">Profit & Loss History</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[700px]">
            <thead>
              <tr className="bg-[#0b1221] text-gray-500 text-xs uppercase tracking-widest font-bold">
                <th className="p-5">Date & Time</th>
                <th className="p-5 text-center">Action</th>
                <th className="p-5 text-right">Amount (₹)</th>
                <th className="p-5 text-right">Previous Bal</th>
                <th className="p-5 text-right">New Bal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {history.map((record, idx) => (
                <tr key={idx} className="hover:bg-[#1e222d]/50 transition-colors">
                  <td className="p-5">
                    <p className="font-bold text-white text-sm">{new Date(record.created_at).toLocaleDateString('en-GB')}</p>
                    <p className="text-xs text-gray-500">{new Date(record.created_at).toLocaleTimeString()}</p>
                  </td>
                  <td className="p-5 text-center">
                    <span className={`inline-flex items-center justify-center font-black text-[10px] px-3 py-1.5 rounded-full tracking-widest ${record.type === 'PROFIT' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-red-500/10 text-red-500 border border-red-500/20'}`}>
                      {record.type}
                    </span>
                  </td>
                  <td className={`p-5 text-right font-black text-lg ${record.type === 'PROFIT' ? 'text-green-500' : 'text-red-500'}`}>
                    {record.type === 'PROFIT' ? '+' : '-'}₹{parseFloat(record.amount).toFixed(2)}
                  </td>
                  <td className="p-5 text-right text-gray-400 font-mono text-sm">₹{parseFloat(record.previous_balance).toFixed(2)}</td>
                  <td className="p-5 text-right">
                    <div className="flex items-center justify-end gap-2 text-white font-mono font-bold text-sm">
                      <ArrowRight size={14} className="text-gray-600" /> ₹{parseFloat(record.new_balance).toFixed(2)}
                    </div>
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-500">
                    <p className="font-bold text-lg mb-1">No Portfolio Activity Yet</p>
                    <p className="text-sm">Profits and losses generated from your trades will appear here.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;